import {useState} from 'react'
import {Card, Badge, Dropdown} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {Droppable, Draggable} from 'react-beautiful-dnd'
import {IKanbanStage, IKanbanTask} from '../../../types/kanban'
import {KanbanCard} from './KanbanCard'

interface Props {
  stage: IKanbanStage
  tasks: IKanbanTask[]
  onAddTask?: (stage: IKanbanStage) => void
  canCreateTask?: boolean
  onOpenAttachments?: (task: IKanbanTask) => void
  onEditStage?: (stage: IKanbanStage) => void
  onDeleteStage?: (stage: IKanbanStage) => void
  onEditTask?: (task: IKanbanTask) => void
  onAddComment?: (task: IKanbanTask) => void
  currentUserEmail?: string
  onArchiveTask?: (task: IKanbanTask) => void
  onDeleteTask?: (task: IKanbanTask) => void
  onQuickAddTask?: (stage: IKanbanStage, title: string) => Promise<void> | void
  quickAddLoading?: boolean
  onOpenReorderModal?: () => void
}

export const getContrastColor = (hexColor?: string | null) => {
  if (!hexColor) return undefined
  const hex = hexColor.replace('#', '')
  if (hex.length !== 6) return undefined
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.7 ? '#000' : '#fff'
}

export const KanbanColumn: React.FC<Props> = ({
  stage,
  tasks,
  onAddTask,
  canCreateTask,
  onOpenAttachments,
  onEditStage,
  onDeleteStage,
  onEditTask,
  onAddComment,
  currentUserEmail,
  onArchiveTask,
  onDeleteTask,
  onQuickAddTask,
  quickAddLoading,
  onOpenReorderModal,
}) => {
  const intl = useIntl()
  const [isHovering, setIsHovering] = useState(false)
  const [quickTaskTitle, setQuickTaskTitle] = useState('')
  const headerStyle = stage.color
    ? {backgroundColor: stage.color, color: getContrastColor(stage.color) ?? '#fff'}
    : undefined
  const handleQuickAdd = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!quickTaskTitle.trim()) return
    if (!onQuickAddTask) return
    await onQuickAddTask(stage, quickTaskTitle.trim())
    setQuickTaskTitle('')
  }

  return (
    <div
      className='kanban-column'
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Card className='kanban-column__card'>
        <Card.Header className='kanban-column__header' style={headerStyle}>
          <Dropdown className='menu-dropdown' align='end' drop='down'>
            <Dropdown.Toggle variant='link' size='sm' className='p-0 text-muted'>
              <i className='bi bi-three-dots-vertical' />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                strategy: 'fixed',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'computeStyles',
                    options: {
                      gpuAcceleration: false,
                    },
                  },
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              }}
              renderOnMount
            >
              <Dropdown.Item onClick={() => onAddTask?.(stage)}>
                <i className='bi bi-plus-circle me-2' />
                <FormattedMessage id='leads.kanban.addTask' defaultMessage='Add task' />
              </Dropdown.Item>
              {onOpenReorderModal && (
                <Dropdown.Item onClick={onOpenReorderModal}>
                  <i className='bi bi-list-ol me-2' />
                  <FormattedMessage id='leads.kanban.reorderColumns' defaultMessage='Reorder columns' />
                </Dropdown.Item>
              )}
              {onEditStage && (
                <Dropdown.Item onClick={() => onEditStage(stage)}>
                  <i className='bi bi-pencil-square me-2' />
                  <FormattedMessage id='leads.kanban.editColumn' defaultMessage='Edit column' />
                </Dropdown.Item>
              )}
              {onDeleteStage && (
                <Dropdown.Item className='text-danger' onClick={() => onDeleteStage(stage)}>
                  <i className='bi bi-trash me-2' />
                  <FormattedMessage id='leads.kanban.deleteColumn' defaultMessage='Delete column' />
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <div className='kanban-column__title-wrapper'>
            <span className='kanban-column__title'>
              {stage.isClosedState && <i className='bi bi-check-circle-fill text-success me-2' />}
              {stage.name}
            </span>
            <Badge bg='light' text='dark' className='kanban-column__badge'>
              {tasks.length}
            </Badge>
          </div>
        </Card.Header>
        <Droppable droppableId={stage.id} type='TASK'>
          {(provided, snapshot) => (
            <>
              <div
                className={`kanban-column__list ${
                  snapshot.isDraggingOver ? 'kanban-column__list--over' : ''
                }`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        className={`kanban-card__wrapper ${
                          dragSnapshot.isDragging ? 'kanban-card__wrapper--dragging' : ''
                        }`}
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <KanbanCard
                          task={task}
                          isClosedStage={stage.isClosedState}
                          onOpenAttachments={onOpenAttachments}
                          onEditTask={onEditTask}
                          onAddComment={onAddComment}
                          currentUserEmail={currentUserEmail}
                          onArchiveTask={onArchiveTask}
                          onDeleteTask={onDeleteTask}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {tasks.length === 0 && (
                  <div className='kanban-column__empty'>
                    <span><FormattedMessage id='leads.kanban.noTasks' defaultMessage='There are no tasks at this stage.' /></span>
                    {canCreateTask && onAddTask && (
                      <button
                        type='button'
                        className='kanban-column__empty-add'
                        onClick={() => onAddTask(stage)}
                      >
                        <i className='bi bi-plus-circle me-2' />
                        <FormattedMessage id='leads.kanban.addTask' defaultMessage='Add task' />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Droppable>
        {canCreateTask && onQuickAddTask && (
          <div
            className={`kanban-column__quick-add ${
              isHovering ? 'kanban-column__quick-add--visible' : ''
            }`}
          >
            <form onSubmit={handleQuickAdd}>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={intl.formatMessage({id: 'leads.kanban.quickAddPlaceholder', defaultMessage: 'Add a quick task...'})}
                value={quickTaskTitle}
                onChange={(e) => setQuickTaskTitle(e.target.value)}
                disabled={quickAddLoading}
              />
              <button
                type='submit'
                className='btn btn-primary btn-sm'
                disabled={quickAddLoading || !quickTaskTitle.trim()}
              >
                {quickAddLoading ? intl.formatMessage({id: 'leads.kanban.adding', defaultMessage: 'Adding...'}) : intl.formatMessage({id: 'leads.kanban.add', defaultMessage: 'Add'})}
              </button>
            </form>
          </div>
        )}
      </Card>
    </div>
  )
}
